import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { ImageUpload } from "../../components/ImageUpload";
import { toast } from "sonner";
import { Save, Home, Info, Phone, LayoutPanelTop, Plus, Trash2, Share2, Instagram, Facebook, Youtube, MessageCircle } from "lucide-react";

const TABS = [
  { key: "home", label: "Homepage", icon: Home },
  { key: "about_page", label: "About Us", icon: Info },
  { key: "contact", label: "Contact", icon: Phone },
  { key: "footer", label: "Footer", icon: LayoutPanelTop },
  { key: "social", label: "Social Media Connect", icon: Share2 },
];

const inputCls = "w-full mt-1 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold";

function Text({ label, value, onChange, testId, area }) {
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      {area ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} rows={3} className={inputCls} />
      ) : (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className={inputCls} />
      )}
    </div>
  );
}

const Card = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-black/5 p-5 mb-5">
    <h3 className="font-serif text-lg text-navy mb-4">{title}</h3>
    <div className="space-y-4">{children}</div>
  </div>
);

export default function AdminContent() {
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("home");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/content").then((r) => setC(r.data)); }, []);

  const upd = (section, path, val) => {
    setC((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let obj = next[section];
      const keys = path.split(".");
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = val;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", { home: c.home, about_page: c.about_page, contact: c.contact, footer: c.footer, social: c.social });
      toast.success("Website content updated — changes are live");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!c) return <div className="text-muted-foreground">Loading content...</div>;

  const home = c.home || {};
  const hero = home.hero || {};
  const combos = home.combos_banner || {};
  const party = home.party_banner || {};
  const about = home.about || {};
  const ap = c.about_page || {};
  const contact = c.contact || {};
  const footer = c.footer || {};
  const social = c.social || {};

  return (
    <div data-testid="admin-content">
      <div className="flex justify-between items-center mb-6 gap-3 flex-wrap">
        <div><h1 className="font-serif text-3xl text-navy">Website Content</h1><p className="text-sm text-muted-foreground">Edit banners, About Us, contact details & links. Saved changes go live instantly.</p></div>
        <button onClick={save} disabled={saving} data-testid="save-content-btn" className="flex items-center gap-2 bg-navy text-cream text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-navy-soft disabled:opacity-60"><Save size={15} /> {saving ? "Saving..." : "Save Changes"}</button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`content-tab-${t.key}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === t.key ? "bg-gold text-navy" : "bg-white border border-black/10 text-navy hover:border-gold"}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "home" && (
        <div className="max-w-3xl">
          <Card title="Hero Section">
            <ImageUpload label="Hero Image" value={hero.image} onChange={(v) => upd("home", "hero.image", v)} testId="hero-image" />
            <Text label="Overline" value={hero.overline} onChange={(v) => upd("home", "hero.overline", v)} testId="hero-overline" />
            <div className="grid sm:grid-cols-2 gap-4">
              <Text label="Title Line 1" value={hero.title_line1} onChange={(v) => upd("home", "hero.title_line1", v)} testId="hero-title1" />
              <Text label="Title Line 2 (gold)" value={hero.title_line2} onChange={(v) => upd("home", "hero.title_line2", v)} testId="hero-title2" />
            </div>
            <Text label="Subtitle" value={hero.subtitle} onChange={(v) => upd("home", "hero.subtitle", v)} testId="hero-subtitle" area />
            <div className="grid sm:grid-cols-2 gap-4">
              <Text label="Primary Button Label" value={hero.primary_label} onChange={(v) => upd("home", "hero.primary_label", v)} testId="hero-primary-label" />
              <Text label="Primary Button Link" value={hero.primary_link} onChange={(v) => upd("home", "hero.primary_link", v)} testId="hero-primary-link" />
              <Text label="Secondary Button Label" value={hero.secondary_label} onChange={(v) => upd("home", "hero.secondary_label", v)} />
              <Text label="Secondary Button Link" value={hero.secondary_link} onChange={(v) => upd("home", "hero.secondary_link", v)} />
              <Text label="Badge Number" value={hero.badge_number} onChange={(v) => upd("home", "hero.badge_number", v)} />
              <Text label="Badge Label" value={hero.badge_label} onChange={(v) => upd("home", "hero.badge_label", v)} />
            </div>
          </Card>

          <Card title="Royal Combos Banner">
            <div className="grid sm:grid-cols-2 gap-4">
              <Text label="Overline" value={combos.overline} onChange={(v) => upd("home", "combos_banner.overline", v)} />
              <Text label="Title" value={combos.title} onChange={(v) => upd("home", "combos_banner.title", v)} testId="combos-title" />
            </div>
            <Text label="Text" value={combos.text} onChange={(v) => upd("home", "combos_banner.text", v)} area />
            <div className="grid sm:grid-cols-2 gap-4">
              <Text label="Button Label" value={combos.button_label} onChange={(v) => upd("home", "combos_banner.button_label", v)} />
              <Text label="Button Link" value={combos.button_link} onChange={(v) => upd("home", "combos_banner.button_link", v)} />
            </div>
          </Card>

          <Card title="Party & Platters Banner">
            <div className="grid sm:grid-cols-2 gap-4">
              <Text label="Overline" value={party.overline} onChange={(v) => upd("home", "party_banner.overline", v)} />
              <Text label="Title" value={party.title} onChange={(v) => upd("home", "party_banner.title", v)} testId="party-title" />
            </div>
            <Text label="Text" value={party.text} onChange={(v) => upd("home", "party_banner.text", v)} area />
            <div className="grid sm:grid-cols-2 gap-4">
              <Text label="Primary Button Label" value={party.primary_label} onChange={(v) => upd("home", "party_banner.primary_label", v)} />
              <Text label="Primary Button Link" value={party.primary_link} onChange={(v) => upd("home", "party_banner.primary_link", v)} />
              <Text label="Secondary Button Label" value={party.secondary_label} onChange={(v) => upd("home", "party_banner.secondary_label", v)} />
              <Text label="Secondary Button Link" value={party.secondary_link} onChange={(v) => upd("home", "party_banner.secondary_link", v)} />
            </div>
          </Card>

          <Card title="Homepage About Block">
            <ImageUpload label="About Image" value={about.image} onChange={(v) => upd("home", "about.image", v)} testId="home-about-image" />
            <Text label="Overline" value={about.overline} onChange={(v) => upd("home", "about.overline", v)} />
            <Text label="Title" value={about.title} onChange={(v) => upd("home", "about.title", v)} />
            <Text label="Text" value={about.text} onChange={(v) => upd("home", "about.text", v)} area />
            <div className="grid sm:grid-cols-2 gap-4">
              <Text label="CTA Label" value={about.cta_label} onChange={(v) => upd("home", "about.cta_label", v)} />
              <Text label="CTA Link" value={about.cta_link} onChange={(v) => upd("home", "about.cta_link", v)} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              {(about.stats || []).map((st, i) => (
                <div key={i} className="border border-black/10 rounded-lg p-2">
                  <Text label={`Stat ${i + 1} Number`} value={st.number} onChange={(v) => upd("home", `about.stats.${i}.number`, v)} />
                  <Text label="Label" value={st.label} onChange={(v) => upd("home", `about.stats.${i}.label`, v)} />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {tab === "about_page" && (
        <div className="max-w-3xl">
          <Card title="Hero">
            <Text label="Hero Title" value={ap.hero_title} onChange={(v) => upd("about_page", "hero_title", v)} testId="ap-hero-title" />
            <Text label="Hero Subtitle" value={ap.hero_subtitle} onChange={(v) => upd("about_page", "hero_subtitle", v)} />
          </Card>
          <Card title="Story Section">
            <div className="grid sm:grid-cols-2 gap-4">
              <ImageUpload label="Image 1" value={ap.image1} onChange={(v) => upd("about_page", "image1", v)} testId="ap-image1" />
              <ImageUpload label="Image 2" value={ap.image2} onChange={(v) => upd("about_page", "image2", v)} testId="ap-image2" />
            </div>
            <Text label="Heading" value={ap.heading} onChange={(v) => upd("about_page", "heading", v)} testId="ap-heading" />
            <Text label="Paragraph 1" value={ap.para1} onChange={(v) => upd("about_page", "para1", v)} area />
            <Text label="Paragraph 2" value={ap.para2} onChange={(v) => upd("about_page", "para2", v)} area />
            <div className="grid sm:grid-cols-2 gap-4">
              <Text label="CTA Label" value={ap.cta_label} onChange={(v) => upd("about_page", "cta_label", v)} />
              <Text label="CTA Link" value={ap.cta_link} onChange={(v) => upd("about_page", "cta_link", v)} />
            </div>
          </Card>
          <GalleryEditor images={ap.gallery || []} onChange={(imgs) => upd("about_page", "gallery", imgs)} />
        </div>
      )}

      {tab === "contact" && (
        <div className="max-w-2xl">
          <Card title="Contact Details">
            <Text label="Address" value={contact.address} onChange={(v) => upd("contact", "address", v)} testId="contact-address" />
            <Text label="Phone" value={contact.phone} onChange={(v) => upd("contact", "phone", v)} testId="contact-phone" />
            <Text label="Email" value={contact.email} onChange={(v) => upd("contact", "email", v)} testId="contact-email" />
            <Text label="Hours" value={contact.hours} onChange={(v) => upd("contact", "hours", v)} />
          </Card>
        </div>
      )}

      {tab === "footer" && (
        <div className="max-w-2xl">
          <Card title="Brand & Social">
            <Text label="Tagline" value={footer.tagline} onChange={(v) => upd("footer", "tagline", v)} />
            <Text label="Description" value={footer.description} onChange={(v) => upd("footer", "description", v)} area />
            <div className="grid sm:grid-cols-3 gap-4">
              <Text label="Instagram URL" value={footer.instagram} onChange={(v) => upd("footer", "instagram", v)} />
              <Text label="Facebook URL" value={footer.facebook} onChange={(v) => upd("footer", "facebook", v)} />
              <Text label="Twitter URL" value={footer.twitter} onChange={(v) => upd("footer", "twitter", v)} />
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <Text label="Address" value={footer.address} onChange={(v) => upd("footer", "address", v)} />
              <Text label="Phone" value={footer.phone} onChange={(v) => upd("footer", "phone", v)} />
              <Text label="Email" value={footer.email} onChange={(v) => upd("footer", "email", v)} />
            </div>
          </Card>
          <LinkEditor title="Explore Links" links={footer.explore_links || []} onChange={(links) => upd("footer", "explore_links", links)} idPrefix="explore" />
          <LinkEditor title="Company Links" links={footer.company_links || []} onChange={(links) => upd("footer", "company_links", links)} idPrefix="company" />
        </div>
      )}

      {tab === "social" && (
        <div className="max-w-2xl">
          <Card title="Social Media Connect">
            <p className="text-sm text-muted-foreground -mt-2">These links power the "Follow us" bar at the top of the website and the footer social icons.</p>
            <div>
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5"><Instagram size={14} className="text-gold" /> Instagram URL</label>
              <input value={social.instagram || ""} onChange={(e) => upd("social", "instagram", e.target.value)} data-testid="social-instagram" placeholder="https://instagram.com/yourpage" className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5"><Facebook size={14} className="text-gold" /> Facebook URL</label>
              <input value={social.facebook || ""} onChange={(e) => upd("social", "facebook", e.target.value)} data-testid="social-facebook" placeholder="https://facebook.com/yourpage" className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5"><MessageCircle size={14} className="text-gold" /> WhatsApp link</label>
              <input value={social.whatsapp || ""} onChange={(e) => upd("social", "whatsapp", e.target.value)} data-testid="social-whatsapp" placeholder="https://wa.me/91XXXXXXXXXX" className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5"><Youtube size={14} className="text-gold" /> YouTube URL</label>
              <input value={social.youtube || ""} onChange={(e) => upd("social", "youtube", e.target.value)} data-testid="social-youtube" placeholder="https://youtube.com/@yourchannel" className={inputCls} />
            </div>
            <Text label="Follow label (top bar text)" value={social.follow_label} onChange={(v) => upd("social", "follow_label", v)} testId="social-follow-label" />
          </Card>
        </div>
      )}
    </div>
  );
}

function GalleryEditor({ images, onChange }) {
  const setImg = (i, url) => onChange(images.map((im, idx) => (idx === i ? url : im)));
  const add = () => onChange([...images, ""]);
  const remove = (i) => onChange(images.filter((_, idx) => idx !== i));
  return (
    <div className="bg-white rounded-xl border border-black/5 p-5 mb-5" data-testid="about-gallery-editor">
      <div className="flex justify-between items-center mb-4">
        <div><h3 className="font-serif text-lg text-navy">Photo Gallery</h3><p className="text-xs text-muted-foreground">Showcase dishes & the kitchen. Shown as a carousel/grid on the About page.</p></div>
        <button onClick={add} data-testid="add-gallery-image" className="flex items-center gap-1 text-xs font-semibold text-navy hover:text-gold"><Plus size={14} /> Add Photo</button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {images.map((img, i) => (
          <div key={i} className="flex gap-2 items-start border border-black/10 rounded-lg p-3" data-testid={`gallery-item-${i}`}>
            <div className="flex-1"><ImageUpload label={`Photo ${i + 1}`} value={img} onChange={(v) => setImg(i, v)} testId={`gallery-image-${i}`} /></div>
            <button onClick={() => remove(i)} data-testid={`remove-gallery-${i}`} className="text-muted-foreground hover:text-destructive mt-5"><Trash2 size={15} /></button>
          </div>
        ))}
        {images.length === 0 && <div className="text-sm text-muted-foreground col-span-2 py-4 text-center">No photos yet. Click "Add Photo" to start your gallery.</div>}
      </div>
    </div>
  );
}

function LinkEditor({ title, links, onChange, idPrefix }) {  const set = (i, key, val) => { const next = links.map((l, idx) => idx === i ? { ...l, [key]: val } : l); onChange(next); };
  const add = () => onChange([...links, { label: "New Link", to: "/" }]);
  const remove = (i) => onChange(links.filter((_, idx) => idx !== i));
  return (
    <div className="bg-white rounded-xl border border-black/5 p-5 mb-5">
      <div className="flex justify-between items-center mb-4"><h3 className="font-serif text-lg text-navy">{title}</h3><button onClick={add} data-testid={`add-${idPrefix}-link`} className="flex items-center gap-1 text-xs font-semibold text-navy hover:text-gold"><Plus size={14} /> Add</button></div>
      <div className="space-y-2">
        {links.map((l, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input value={l.label} onChange={(e) => set(i, "label", e.target.value)} placeholder="Label" className="flex-1 border border-black/15 rounded-lg px-3 py-2 text-sm" />
            <input value={l.to} onChange={(e) => set(i, "to", e.target.value)} placeholder="/path" className="flex-1 border border-black/15 rounded-lg px-3 py-2 text-sm" />
            <button onClick={() => remove(i)} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
